import { useMemo, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { getUserRoleName, User, UserRole } from "features/auth/types/user";
import { useGetAllCompaniesQuery } from "app/api/companies";
import { Navigate } from "react-router-dom";
import Spinner from "features/spinner/Spinner";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useCreateUserMutation, useGetAllUsersQuery, useUpdateUserMutation } from "app/api/users";
import { useAuthContext } from "features/auth/AuthContext";
import { toast } from "react-toastify";
import LargeDialog from "components/LargeDialog";
import { Check, CheckBox, CheckBoxOutlineBlank, Clear } from "@mui/icons-material";
import { CreateUserRequest } from "api/users/createUser";
import { useGetAllDepartmentsQuery } from "app/api/departments";
import { useGetAllOfficesQuery } from "app/api/offices";
import { getErrorMessage } from "utilities/getErrorMessage";

type UpsertUserDialogProps = {
  create?: boolean;
  onClose: () => void;
} & (
  | {
      create: true;
      user?: never;
    }
  | {
      create?: false;
      user: User;
    }
);

const UpsertUserDialog: React.FC<UpsertUserDialogProps> = ({
  create,
  user: userToEdit,
  onClose,
}) => {
  const { user } = useAuthContext();

  const { data: companies, isLoading: companiesLoading } = useGetAllCompaniesQuery();
  const { data: departments, isLoading: departmentsLoading } = useGetAllDepartmentsQuery();
  const { data: offices, isLoading: officesLoading } = useGetAllOfficesQuery();

  const { data: admins } = useGetAllUsersQuery({ roles: [UserRole.ADMIN], inactive: true });
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isEditing }] = useUpdateUserMutation();

  const [useDifferentAppleId, setUseDifferentAppleId] = useState<boolean>(!!userToEdit?.appleId);

  const sortedAdmins = useMemo(() => {
    if (!admins) return [];
    return [...admins].sort((a, b) =>
      a.userName.toLowerCase() > b.userName.toLowerCase() ? 1 : -1
    );
  }, [admins]);

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateUserRequest>();

  const onSubmit: SubmitHandler<CreateUserRequest> = (data) => {
    const requestData = {
      userName: data.userName,
      email: data.email,
      appleId: (useDifferentAppleId && data.appleId) || null,
      phone: data.phone,
      role: data.role,
      departmentIds: data.departmentIds || null,
      officeId: data.officeId || null,
      anstnrHogia: data.anstnrHogia || null,
      controllerId: data.controllerId || null,
      pyramidSignature: data.pyramidSignature || null,
      articleTime: data.articleTime || null,
      articleTimeInCar: data.articleTimeInCar || null,
      companyId: data.companyId || null,

      admin: data.role === UserRole.ADMIN,
      coworker: data.role === UserRole.EMPLOYEE,
      econ: data.role === UserRole.PAYROLL,
      externalInstaller: data.role === UserRole.EXTERNAL_INSTALLER,
      internalInstaller: data.role === UserRole.INTERNAL_INSTALLER,
      sales: data.role === UserRole.SALES,

      ...(![UserRole.ADMIN, UserRole.USER_ADMIN, UserRole.SALES].includes(data.role) && {
        pyramidSignature: null,
      }),

      ...(data.role !== UserRole.INTERNAL_INSTALLER && {
        articleTime: null,
        articleTimeInCar: null,
      }),

      ...(data.role !== UserRole.EXTERNAL_INSTALLER && {
        companyId: null,
      }),

      ...(data.role === UserRole.EXTERNAL_INSTALLER && {
        anstnrHogia: null,
        departmentIds: null,
        officeId: null,
      }),
    } as CreateUserRequest;

    (create ? createUser(requestData) : updateUser({ id: userToEdit!.id, ...requestData }))
      .unwrap()
      .then(() => {
        toast.success(`Användaren ${create ? "skapades" : "uppdaterades"}`);
        onClose();
      })
      .catch((e) => toast.error(getErrorMessage(e)));
  };

  const { role: selectedRole } = watch();

  const getAvailableRoles = (): UserRole[] => {
    if (user?.role === UserRole.ADMIN) {
      return [
        UserRole.ADMIN,
        UserRole.USER_ADMIN,
        UserRole.INTERNAL_INSTALLER,
        UserRole.EXTERNAL_INSTALLER,
        UserRole.EMPLOYEE,
        UserRole.PAYROLL,
        UserRole.SALES,
      ];
    } else if (user?.role === UserRole.USER_ADMIN && userToEdit?.role !== UserRole.ADMIN) {
      return [
        UserRole.USER_ADMIN,
        UserRole.INTERNAL_INSTALLER,
        UserRole.EXTERNAL_INSTALLER,
        UserRole.EMPLOYEE,
        UserRole.PAYROLL,
        UserRole.SALES,
      ];
    } else if (user?.role === UserRole.USER_ADMIN && userToEdit?.role === UserRole.ADMIN) {
      return [UserRole.ADMIN];
    } else {
      return [
        UserRole.INTERNAL_INSTALLER,
        UserRole.EXTERNAL_INSTALLER,
        UserRole.EMPLOYEE,
        UserRole.PAYROLL,
      ];
    }
  };

  if (user?.role !== UserRole.ADMIN && user?.role !== UserRole.USER_ADMIN)
    return <Navigate to="/users" />;

  return (
    <LargeDialog
      title={create ? "Skapa användare" : "Redigera användare"}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isCreating || isEditing}
            startIcon={<Clear />}
          >
            Avbryt
          </Button>

          <Button
            type="submit"
            variant="contained"
            loading={isCreating || isEditing}
            color="success"
            startIcon={<Check />}
          >
            {create ? "Skapa" : "Spara"}
          </Button>
        </>
      }
    >
      {companiesLoading || departmentsLoading || officesLoading ? (
        <Box sx={{ display: "grid", placeItems: "center", height: "1rem" }}>
          <Spinner size="3rem" color="#5F5F5F" />
        </Box>
      ) : (
        <>
          <Controller
            name="userName"
            control={control}
            defaultValue={userToEdit?.userName}
            rules={{ required: "Ange för- och efternamn" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="För- och efternamn"
                type="text"
                autoComplete="new-password"
                variant="filled"
                error={!!errors.userName}
                helperText={errors.userName?.message}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            defaultValue={userToEdit?.email}
            rules={{ required: "Alla användare måste ha en e-post" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="E-post"
                type="email"
                autoComplete="new-password"
                variant="filled"
                spellCheck={false}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />

          <FormControlLabel
            label="Använd en annan E-post för AppleId"
            control={
              <Checkbox
                checked={useDifferentAppleId}
                onChange={() => setUseDifferentAppleId(!useDifferentAppleId)}
              />
            }
          />

          {useDifferentAppleId && (
            <Controller
              name="appleId"
              control={control}
              defaultValue={userToEdit?.appleId}
              rules={{ required: "Ange e-post för Apple-ID" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="E-post för Apple-ID"
                  type="email"
                  autoComplete="new-password"
                  variant="filled"
                  spellCheck={false}
                  error={!!errors.appleId}
                  helperText={errors.appleId?.message}
                />
              )}
            />
          )}

          <Controller
            name="phone"
            control={control}
            defaultValue={userToEdit?.phone}
            rules={{ required: "Ange ett telefonnummer" }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Telefonnummer"
                type="tel"
                autoComplete="new-password"
                variant="filled"
                error={!!errors.phone}
                helperText={errors.phone?.message}
              />
            )}
          />

          <Controller
            name="role"
            control={control}
            defaultValue={userToEdit?.role}
            rules={{ required: "Välj en roll" }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Roll"
                autoComplete="new-password"
                variant="filled"
                error={!!errors.role}
                defaultValue={field.value || ""}
                helperText={errors.role?.message}
                disabled={user?.role === UserRole.USER_ADMIN && userToEdit?.role === UserRole.ADMIN}
              >
                <MenuItem value="" disabled>
                  Välj roll
                </MenuItem>
                {getAvailableRoles().map((role) => (
                  <MenuItem key={role} value={role}>
                    {getUserRoleName(role)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {selectedRole !== UserRole.NONE && selectedRole !== UserRole.EXTERNAL_INSTALLER && (
            <>
              <Controller
                name="departmentIds"
                control={control}
                defaultValue={userToEdit?.departments.map((d) => d.id) || []}
                rules={{ required: "Medarbetare måste tillhöra minst en avdelning" }}
                render={({ field }) => (
                  <FormControl variant="filled" error={!!errors.departmentIds}>
                    <InputLabel id="departmentIds">Avdelning</InputLabel>
                    <Select
                      {...field}
                      labelId="departmentIds"
                      multiple
                      renderValue={(selected) =>
                        departments
                          ?.filter((department) => selected?.includes(department.id))
                          .map((department) => department.name)
                          .join(", ")
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 48 * 4.5 + 8,
                            width: 250,
                          },
                        },
                      }}
                    >
                      {departments?.map((department) => {
                        const selected = field.value?.includes(department.id) ?? false;
                        const SelectionIcon = selected ? CheckBox : CheckBoxOutlineBlank;

                        return (
                          <MenuItem key={department.id} value={department.id}>
                            <SelectionIcon
                              fontSize="small"
                              style={{ marginRight: 8, padding: 9, boxSizing: "content-box" }}
                            />
                            <ListItemText primary={department.name} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>{errors.departmentIds?.message}</FormHelperText>
                  </FormControl>
                )}
              />

              <Controller
                name="officeId"
                control={control}
                defaultValue={userToEdit?.office?.id || null}
                rules={{ required: "Medarbetare måste tillhöra ett kontor" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Kontor"
                    autoComplete="new-password"
                    variant="filled"
                    value={field.value || ""}
                    error={!!errors.officeId}
                    helperText={errors.officeId?.message}
                  >
                    <MenuItem value="" disabled>
                      Välj kontor
                    </MenuItem>
                    {offices?.map((office) => (
                      <MenuItem key={office.id} value={office.id}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />

              <Controller
                name="anstnrHogia"
                control={control}
                defaultValue={userToEdit?.anstnrHogia || ""}
                rules={{ required: "Medarbetare måste ha ett anställningsnummer" }}
                render={({ field }) => (
                  <TextField
                    label="Anställningsnummer"
                    type="number"
                    autoComplete="new-password"
                    variant="filled"
                    error={!!errors.anstnrHogia}
                    helperText={errors.anstnrHogia?.message}
                    {...field}
                  />
                )}
              />

              <Controller
                name="controllerId"
                control={control}
                defaultValue={userToEdit?.controllerId || ""}
                rules={{ required: "Medarbetare måste ha en attesterare" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Attesterare"
                    autoComplete="new-password"
                    variant="filled"
                    error={!!errors.controllerId}
                    helperText={errors.controllerId?.message}
                  >
                    <MenuItem value="" disabled>
                      Välj attesterare
                    </MenuItem>
                    {sortedAdmins.map((user) => (
                      <MenuItem key={user.id} value={user.id} hidden={user.state === "INACTIVE"}>
                        {user.userName}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </>
          )}

          {(selectedRole === UserRole.ADMIN ||
            selectedRole === UserRole.USER_ADMIN ||
            selectedRole === UserRole.SALES) && (
            <Controller
              name="pyramidSignature"
              control={control}
              defaultValue={userToEdit?.pyramidSignature}
              rules={{
                pattern: { value: /^[A-Ö]+$/, message: "Endast versaler (A-Ö) är tillåtna" },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Pyramid-signatur"
                  type="text"
                  autoComplete="new-password"
                  variant="filled"
                  slotProps={{ htmlInput: { maxLength: 16 } }}
                  error={!!errors.pyramidSignature}
                  helperText={errors.pyramidSignature?.message}
                />
              )}
            />
          )}

          {selectedRole === UserRole.INTERNAL_INSTALLER && (
            <>
              <Controller
                name="articleTime"
                control={control}
                defaultValue={userToEdit?.articleTime}
                rules={{ required: "Krävs för interna installatörer" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Tidkod (installation)"
                    type="text"
                    autoComplete="new-password"
                    variant="filled"
                    error={!!errors.articleTime}
                    helperText={errors.articleTime?.message}
                  />
                )}
              />

              <Controller
                name="articleTimeInCar"
                control={control}
                defaultValue={userToEdit?.articleTimeInCar}
                rules={{ required: "Krävs för interna installatörer" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Tidkod (restid i bil)"
                    type="text"
                    autoComplete="new-password"
                    variant="filled"
                    error={!!errors.articleTimeInCar}
                    helperText={errors.articleTimeInCar?.message}
                  />
                )}
              />
            </>
          )}

          {selectedRole === UserRole.EXTERNAL_INSTALLER && (
            <Controller
              name="companyId"
              control={control}
              defaultValue={userToEdit?.companyId}
              rules={{ required: "Välj ett företag" }}
              render={({ field }) => (
                <TextField
                  select
                  label="Företag"
                  autoComplete="new-password"
                  variant="filled"
                  error={!!errors.companyId}
                  helperText={errors.companyId?.message}
                  defaultValue={field.value || ""}
                  {...field}
                >
                  <MenuItem value="" disabled>
                    Välj företag
                  </MenuItem>
                  {companies?.map((company) => (
                    <MenuItem
                      key={company.id}
                      value={company.id}
                      selected={company.id === userToEdit?.companyId && true}
                    >
                      {company.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          )}
        </>
      )}
    </LargeDialog>
  );
};

export default UpsertUserDialog;
